import { FC, ReactElement, useState } from 'react';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import i18next from 'i18next';
import { ExerciseDto } from '../../../../../../shared/dtos/exercise.dto';
import { ScopeKey } from '../../../../util/types';
import ExercisePickerCard from './ExercisePickerCard';
import PrimaryButton from '../../../../components/PrimaryButton';
import EmptyState from '../../../../components/EmptyState';
import { EXERCISE_PAGE_SIZE } from '../../../../hooks/useExerciseSearch';

type ExercisePickerResultsProps = {
  scope: ScopeKey;
  exercises: ExerciseDto[];
  totalCount: number;
  selectedIds: Set<string>;
  isLoading: boolean;
  isError: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  hasActiveFilters: boolean;
  onToggleSelection: (exercise: ExerciseDto) => void;
  onLoadMore: () => void;
  onRetry: () => void;
  onResetFilters: () => void;
};

const ExercisePickerResults: FC<ExercisePickerResultsProps> = ({
  scope,
  exercises,
  totalCount,
  selectedIds,
  isLoading,
  isError,
  isFetchingNextPage,
  hasNextPage,
  hasActiveFilters,
  onToggleSelection,
  onLoadMore,
  onRetry,
  onResetFilters,
}: ExercisePickerResultsProps): ReactElement => {
  const [playingExerciseId, setPlayingExerciseId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
        <div className="text-muted mt-2">{i18next.t('generic.loading')}</div>
      </div>
    );
  }

  if (isError) {
    return (
      <>
        <EmptyState icon="fas fa-triangle-exclamation" message={i18next.t('exerciseModal.loadError')} />

        <div className="text-center mt-3">
          <PrimaryButton onClick={onRetry}>{i18next.t('generic.retry')}</PrimaryButton>
        </div>
      </>
    );
  }

  if (exercises.length === 0) {
    const showsFilterHint: boolean = hasActiveFilters || scope === 'core';
    const emptyText: string = showsFilterHint
      ? i18next.t('exerciseModal.noResults')
      : i18next.t('exerciseModal.noTenantExercises');

    return (
      <>
        <EmptyState icon="fas fa-dumbbell" message={emptyText} />

        {hasActiveFilters && (
          <div className="text-center mt-2">
            <Button variant="link" onClick={onResetFilters}>
              {i18next.t('generic.resetFilter')}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <p className="text-muted small mb-2">
        {i18next.t('exerciseModal.resultCount', { loaded: exercises.length, total: totalCount })}
      </p>

      <Row className="g-3">
        {exercises.map((exercise: ExerciseDto) => (
          <Col xs={12} md={6} xl={4} xxl={3} key={`${scope}-${exercise.id}`}>
            <ExercisePickerCard
              exercise={exercise}
              isSelected={selectedIds.has(exercise.id)}
              isPlaying={playingExerciseId === exercise.id}
              onToggleSelection={onToggleSelection}
              onRequestPlay={(selected: ExerciseDto) => setPlayingExerciseId(selected.id)}
            />
          </Col>
        ))}
      </Row>

      {hasNextPage && (
        <div className="text-center mt-4">
          <PrimaryButton onClick={onLoadMore} disabled={isFetchingNextPage}>
            {isFetchingNextPage && <Spinner animation="border" size="sm" className="me-2" />}
            {i18next.t('exerciseModal.loadMore', {
              count: Math.min(totalCount - exercises.length, EXERCISE_PAGE_SIZE),
            })}
          </PrimaryButton>
        </div>
      )}
    </>
  );
};

export default ExercisePickerResults;
